import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Tabs } from '@/components/UI/Tabs';
import { StatCard } from '@/components/UI/StatCard';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Chip } from '@/components/UI/Chip';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Pagination } from '@/components/UI/Pagination';
import { Alert } from '@/components/UI/Alert';
import { Switch } from '@/components/UI/Switch';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import {
  useGetRequestsSummaryQuery,
  useListRequestsQuery,
  useGetRequestQuery,
  useRetryRequestMutation,
  useCancelRequestMutation,
  useDiscardRequestMutation,
  useListDirectoryQuery,
  useUpdateDirectoryEntryMutation,
  useListTemplatesQuery,
  useUpdateTemplateMutation,
  type NotificationDirectoryEntry,
  type NotificationRequest,
  type NotificationTemplate,
} from '@/redux/internal-services/notificationsAdminApi';
import {
  useGetConfigurationQuery,
  useUpdateConfigurationMutation,
} from '@/redux/internal-services/onboardingApi';

// ── Shared helpers ────────────────────────────────────────────────────────────

const ALL_CHANNELS = ['email', 'sms', 'push', 'in_app'] as const;
const CLASSIFICATIONS = ['Critical', 'Important', 'Info'] as const;

const CLASSIFICATION_VARIANT: Record<string, BadgeProps['variant']> = {
  Critical: 'danger',
  Important: 'warning',
  Info: 'accent',
};

function ChannelBadge({ ch, blue }: { ch: string; blue?: boolean }) {
  return <Badge variant={blue ? 'accent' : 'neutral'}>{ch}</Badge>;
}

function ChannelCheckboxGroup({
  label,
  selected,
  onChange,
  disabledItems,
}: {
  label: string;
  selected: string[];
  onChange: (v: string[]) => void;
  disabledItems?: string[];
}) {
  const toggle = (ch: string) => {
    if (disabledItems?.includes(ch)) return;
    onChange(selected.includes(ch) ? selected.filter((x) => x !== ch) : [...selected, ch]);
  };
  return (
    <div>
      {label && <p className="mb-1 text-xs font-medium text-ui-text">{label}</p>}
      <div className="flex flex-wrap gap-2">
        {ALL_CHANNELS.map((ch) => (
          <Chip
            key={ch}
            selected={selected.includes(ch)}
            onClick={() => toggle(ch)}
            disabled={disabledItems?.includes(ch)}
          >
            {ch}
          </Chip>
        ))}
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
// Keys are the raw lowercase values RequestStatus/DeliveryStatus store in Mongo —
// unlike Classification/ActionStatus, these enums are never Title-Case.
const STATUS_STYLES: Record<string, { label: string; variant: BadgeProps['variant'] }> = {
  requested: { label: 'Requested', variant: 'neutral' },
  pending: { label: 'Pending', variant: 'warning' },
  queued: { label: 'Queued', variant: 'accent' },
  scheduled: { label: 'Scheduled', variant: 'accent' },
  sending: { label: 'Sending', variant: 'warning' },
  dispatching: { label: 'Dispatching', variant: 'warning' },
  sent: { label: 'Delivered', variant: 'success' },
  partial: { label: 'Partial', variant: 'warning' },
  retried: { label: 'Retried', variant: 'warning' },
  failed: { label: 'Failed', variant: 'danger' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
  expired: { label: 'Expired', variant: 'neutral' },
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? { label: status || '—', variant: 'neutral' as BadgeProps['variant'] };
  return <Badge variant={style.variant}>{style.label}</Badge>;
}

const CLOSE_ICON = (
  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
  </svg>
);

// ── Requests Tab ──────────────────────────────────────────────────────────────

function RequestsTab() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const { data: summary } = useGetRequestsSummaryQuery();
  const { data, isFetching } = useListRequestsQuery({ status: statusFilter || undefined, limit, skip });
  const { data: detail, isFetching: isDetailFetching } = useGetRequestQuery(selectedId!, { skip: !selectedId });

  const [retry] = useRetryRequestMutation();
  const [cancel] = useCancelRequestMutation();
  const [discard] = useDiscardRequestMutation();

  const requests = data?.data ?? [];
  const total = data?.total ?? 0;

  const counts = summary?.data;

  return (
    <div className="flex flex-col gap-4">
      {/* Summary cards */}
      {counts && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Total" value={counts.total} />
          <StatCard label="Delivered" value={counts.delivered} tone="success" />
          <StatCard label="Dispatch Failed" value={counts.dispatch_failed} tone="danger" />
          <StatCard label="Queue Failed" value={counts.queue_failed} tone="warning" />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
          className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
        >
          <option value="">All statuses</option>
          {[
            { value: 'requested', label: 'Requested' },
            { value: 'queued', label: 'Queued' },
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'sent', label: 'Delivered' },
            { value: 'partial', label: 'Partial' },
            { value: 'failed', label: 'Failed' },
            { value: 'cancelled', label: 'Cancelled' },
            { value: 'expired', label: 'Expired' },
          ].map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <span className="text-xs text-ui-text3">{total} results</span>
      </div>

      {/* Table */}
      <div className="table-card">
        {isFetching ? (
          <LoadingState label="Loading..." />
        ) : requests.length === 0 ? (
          <EmptyState title="No requests found." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th-cell">Code</th>
                  <th className="th-cell">Status</th>
                  <th className="th-cell">Classification</th>
                  <th className="th-cell">Channels</th>
                  <th className="th-cell">Recipient</th>
                  <th className="th-cell">Created</th>
                  <th className="th-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r: NotificationRequest) => (
                  <React.Fragment key={r.id}>
                    <tr className={cn('transition-colors hover:bg-ui-hover', r.id === selectedId && 'bg-ui-hover')}>
                      <td className="td-cell font-mono text-xs">{r.notification_code}</td>
                      <td className="td-cell"><StatusBadge status={r.status ?? ''} /></td>
                      <td className="td-cell text-xs text-ui-text2">{r.classification}</td>
                      <td className="td-cell">
                        <div className="flex flex-wrap gap-1">
                          {(r.resolved_channels ?? []).map((ch) => <ChannelBadge key={ch} ch={ch} />)}
                        </div>
                      </td>
                      <td className="td-cell text-xs text-ui-text2">
                        {r.recipient?.to_email ?? r.recipient?.to_phone ?? r.recipient?.user_id ?? '—'}
                      </td>
                      <td className="td-cell text-xs text-ui-text3">
                        {r.created_on ? new Date(r.created_on).toLocaleString() : '—'}
                      </td>
                      <td className="td-cell">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="link"
                            size="xs"
                            className={cn('text-accent-text', r.id === selectedId && 'font-semibold')}
                            onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                          >
                            {r.id === selectedId ? 'Hide' : 'Detail'}
                          </Button>
                          {(r.status === 'failed' || r.queue_status === 'failed') && (
                            <Button variant="link" size="xs" className="text-success-text" onClick={() => retry(r.id)}>
                              Retry
                            </Button>
                          )}
                          {(r.status === 'queued' || r.status === 'scheduled') && (
                            <Button variant="link" size="xs" className="text-warning-text" onClick={() => cancel(r.id)}>
                              Cancel
                            </Button>
                          )}
                          <Button variant="link" size="xs" className="text-danger-text" onClick={() => discard(r.id)}>
                            Discard
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {r.id === selectedId && (
                      <tr className="bg-ui-subtle">
                        <td colSpan={7} className="border-b border-ui-border px-6 py-4">
                          {isDetailFetching || !detail?.data ? (
                            <p className="text-xs text-ui-text3">Loading…</p>
                          ) : (
                            <div className="flex flex-col gap-3">
                              <div className="grid grid-cols-3 gap-3 text-xs text-ui-text2">
                                <div><span className="font-medium">ID:</span> {detail.data.id}</div>
                                <div><span className="font-medium">Code:</span> {detail.data.notification_code}</div>
                                <div><span className="font-medium">Status:</span> {detail.data.status}</div>
                                <div><span className="font-medium">Delivery mode:</span> {detail.data.delivery_mode}</div>
                                <div><span className="font-medium">Attempts:</span> {detail.data.attempts_count}</div>
                                <div><span className="font-medium">Sent:</span> {detail.data.sent_on ? new Date(detail.data.sent_on).toLocaleString() : '—'}</div>
                              </div>
                              {detail.data.last_error && (
                                <p className="rounded-chip bg-danger/10 px-3 py-2 font-mono text-xs text-danger-text">{detail.data.last_error}</p>
                              )}
                              {(detail.data.delivery_logs ?? []).length > 0 && (
                                <div>
                                  <p className="mb-2 text-xs font-medium text-ui-text">Delivery Logs</p>
                                  <div className="space-y-1">
                                    {detail.data.delivery_logs!.map((log) => (
                                      <div key={log.id} className="flex items-center gap-3 rounded-chip border border-ui-border bg-ui-card px-3 py-1.5 text-xs">
                                        <ChannelBadge ch={log.channel ?? ''} />
                                        <StatusBadge status={log.delivery_status ?? ''} />
                                        <span className="text-ui-text2">{log.recipient_address}</span>
                                        {log.failure_reason && <span className="text-danger-text">{log.failure_reason}</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex flex-col items-center gap-1 text-xs text-ui-text2">
          <span>Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
          <Pagination
            page={Math.floor(skip / limit)}
            totalPages={Math.ceil(total / limit)}
            onPageChange={(p) => setSkip(p * limit)}
            previousLabel="Prev"
            className="pt-0"
          />
        </div>
      )}

    </div>
  );
}

// ── Templates Tab ─────────────────────────────────────────────────────────────

function TemplatesTab() {
  const [codeFilter, setCodeFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [saveOk, setSaveOk] = useState(false);

  const { data, isFetching } = useListTemplatesQuery({
    notification_code: codeFilter || undefined,
    channel: channelFilter || undefined,
  });

  const [updateTemplate] = useUpdateTemplateMutation();

  const templates = data?.data ?? [];

  const openEdit = (t: NotificationTemplate) => {
    setSaveOk(false);
    setEditing(t);
    setEditSubject(t.subject_template ?? '');
    setEditBody(t.body_template ?? '');
    setEditActive(t.is_active);
  };

  const closeEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    const body: Record<string, unknown> = {};
    if (editSubject !== editing.subject_template) body.subject_template = editSubject;
    if (editBody !== editing.body_template) body.body_template = editBody;
    if (editActive !== editing.is_active) body.is_active = editActive;
    if (Object.keys(body).length === 0) { closeEdit(); return; }
    try {
      await updateTemplate({ id: editing.id, body }).unwrap();
      setSaveOk(true);
      setTimeout(closeEdit, 900);
    } catch {
      // save failed: the drawer stays open so the admin can retry
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Filter by notification code..."
          aria-label="Filter by notification code"
          value={codeFilter}
          onChange={(e) => setCodeFilter(e.target.value)}
          className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
        />
        <select
          aria-label="Filter by channel"
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
        >
          <option value="">All channels</option>
          {ALL_CHANNELS.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
        </select>
      </div>

      <div className="table-card">
        {isFetching ? (
          <LoadingState label="Loading..." />
        ) : templates.length === 0 ? (
          <EmptyState title="No templates found." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th-cell">Code</th>
                  <th className="th-cell">Channel</th>
                  <th className="th-cell">Language</th>
                  <th className="th-cell">Version</th>
                  <th className="th-cell">Active</th>
                  <th className="th-cell">Tenant</th>
                  <th className="th-cell">Action</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t: NotificationTemplate) => (
                  <tr key={t.id} className="transition-colors hover:bg-ui-hover">
                    <td className="td-cell font-mono text-xs">{t.notification_code}</td>
                    <td className="td-cell"><ChannelBadge ch={t.channel ?? ''} blue /></td>
                    <td className="td-cell text-xs text-ui-text2">{t.language}</td>
                    <td className="td-cell text-xs text-ui-text2">v{t.version}</td>
                    <td className="td-cell">
                      <Badge variant={t.is_active ? 'success' : 'neutral'}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="td-cell text-xs text-ui-text3">{t.tenant_id ?? 'System'}</td>
                    <td className="td-cell">
                      <Button variant="link" size="xs" className="text-accent-text" onClick={() => openEdit(t)}>Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-drawer flex justify-end">
          <div className="modal-scrim absolute inset-0" onClick={closeEdit} />
          <div className="relative flex w-full max-w-lg flex-col overflow-y-auto bg-ui-card shadow-pop">
            <div className="flex items-center justify-between border-b border-ui-border px-5 py-4">
              <div>
                <p className="font-mono text-xs text-ui-text2">{editing.notification_code}</p>
                <p className="text-sm font-semibold text-ui-text">Edit Template</p>
              </div>
              <IconButton label="Close" size="sm" onClick={closeEdit}>
                {CLOSE_ICON}
              </IconButton>
            </div>
            <div className="flex flex-1 flex-col gap-5 px-5 py-4">
              <div className="flex gap-3 text-xs text-ui-text2">
                <ChannelBadge ch={editing.channel ?? ''} blue />
                <span>Lang: {editing.language}</span>
                <span>v{editing.version}</span>
              </div>

              <div>
                <label htmlFor="tpl-subject" className="mb-1 block text-xs font-medium text-ui-text">Subject Template</label>
                <input
                  id="tpl-subject"
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  className={fieldClass()}
                />
              </div>

              <div>
                <label htmlFor="tpl-body" className="mb-1 block text-xs font-medium text-ui-text">Body Template</label>
                <textarea
                  id="tpl-body"
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={8}
                  className={cn(fieldClass({ shape: 'box' }), 'font-mono')}
                />
              </div>

              <div className="flex items-center justify-between rounded-control border border-ui-border bg-ui-subtle px-4 py-3">
                <div>
                  <p className="text-xs font-medium text-ui-text">Active</p>
                  <p className="text-xs text-ui-text3">Inactive templates are not used for dispatch.</p>
                </div>
                <Switch checked={editActive} onCheckedChange={setEditActive} aria-label="Active" />
              </div>

              {editing.required_placeholders.length > 0 && (
                <div className="rounded-control border border-ui-border bg-ui-subtle px-4 py-3">
                  <p className="mb-1 text-xs font-medium text-ui-text">Required Placeholders</p>
                  <div className="flex flex-wrap gap-1">
                    {editing.required_placeholders.map((p) => (
                      <span key={p} className="rounded-chip border border-ui-border bg-ui-card px-1.5 py-0.5 font-mono text-xs text-ui-text2">{`{{${p}}}`}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 border-t border-ui-border px-5 py-4">
              <Button
                variant="accent"
                className={cn('flex-1', saveOk && 'bg-success-fill hover:shadow-none')}
                onClick={handleSave}
              >
                {saveOk ? 'Saved!' : 'Save'}
              </Button>
              <Button variant="outline" className="flex-1" onClick={closeEdit}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Directory Tab ─────────────────────────────────────────────────────────────

function DirectoryTab() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');

  const [editing, setEditing] = useState<NotificationDirectoryEntry | null>(null);
  const [editClassification, setEditClassification] = useState('');
  const [editMandatory, setEditMandatory] = useState<string[]>([]);
  const [, setEditOptional] = useState<string[]>([]);
  const [editTenantOverride, setEditTenantOverride] = useState(true);
  const [editIsActive, setEditIsActive] = useState(true);
  const [saveOk, setSaveOk] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const { data, isFetching } = useListDirectoryQuery({
    search: search || undefined,
    channel: channelFilter || undefined,
    classification: classFilter || undefined,
  });

  const [updateEntry] = useUpdateDirectoryEntryMutation();

  const entries = data?.data ?? [];

  const openEdit = (e: NotificationDirectoryEntry) => {
    setSaveOk(false);
    setShowGuide(false);
    setEditing(e);
    setEditClassification(e.default_classification ?? e.classification ?? '');
    setEditMandatory(e.mandatory_channels ?? []);
    setEditOptional((e.optional_channels ?? []).filter((c) => !(e.mandatory_channels ?? []).includes(c)));
    setEditTenantOverride(e.tenant_override_allowed ?? true);
    setEditIsActive(e.is_active);
  };

  const closeEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    const body: {
      default_classification?: string | null;
      tenant_override_allowed?: boolean;
      mandatory_channels?: string[];
      is_active?: boolean;
    } = {};

    const origClassification = editing.default_classification ?? editing.classification ?? '';
    if (editClassification !== origClassification) body.default_classification = editClassification || null;

    if (editTenantOverride !== (editing.tenant_override_allowed ?? true)) body.tenant_override_allowed = editTenantOverride;

    if (!editTenantOverride) {
      const origMandatory = JSON.stringify([...(editing.mandatory_channels ?? [])].sort());
      if (JSON.stringify([...editMandatory].sort()) !== origMandatory) body.mandatory_channels = editMandatory;
    }

    if (editIsActive !== editing.is_active) body.is_active = editIsActive;

    if (Object.keys(body).length === 0) { closeEdit(); return; }

    try {
      await updateEntry({ notification_code: editing.notification_code!, body }).unwrap();
      setSaveOk(true);
      setTimeout(closeEdit, 900);
    } catch {
      // save failed: the drawer stays open so the admin can retry
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search code or name..."
          aria-label="Search code or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
        />
        <select
          aria-label="Filter by channel"
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
        >
          <option value="">All channels</option>
          {ALL_CHANNELS.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
        </select>
        <select
          aria-label="Filter by classification"
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
          className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
        >
          <option value="">All classifications</option>
          {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-ui-text3">{entries.length} entries</span>
      </div>

      {/* Table */}
      <div className="table-card">
        {isFetching ? (
          <LoadingState label="Loading..." />
        ) : entries.length === 0 ? (
          <EmptyState title="No entries found." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th-cell">Code</th>
                  <th className="th-cell">Classification</th>
                  <th className="th-cell">Mandatory Channels</th>
                  <th className="th-cell">Optional Channels</th>
                  <th className="th-cell text-center">System</th>
                  <th className="th-cell">Active</th>
                  <th className="th-cell">Action</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e: NotificationDirectoryEntry) => {
                  const classification = e.default_classification ?? e.classification;

                  return (
                    <tr key={e.id} className="transition-colors hover:bg-ui-hover">
                      <td className="td-cell font-mono text-xs">{e.id}</td>
                      <td className="td-cell">
                        {classification ? (
                          <Badge variant={CLASSIFICATION_VARIANT[classification] ?? 'neutral'}>
                            {classification}
                          </Badge>
                        ) : (
                          <span className="text-xs text-ui-text3">—</span>
                        )}
                      </td>
                      <td className="td-cell">
                        <div className="flex flex-wrap gap-1">
                          {(e.mandatory_channels ?? []).map((ch) => <ChannelBadge key={ch} ch={ch} />)}
                          {(e.mandatory_channels ?? []).length === 0 && <span className="text-xs text-ui-text3">—</span>}
                        </div>
                      </td>
                      <td className="td-cell">
                        <div className="flex flex-wrap gap-1">
                          {(e.optional_channels ?? []).map((ch) => <ChannelBadge key={ch} ch={ch} blue />)}
                          {(e.optional_channels ?? []).length === 0 && <span className="text-xs text-ui-text3">—</span>}
                        </div>
                      </td>
                      <td className="td-cell text-center">
                        {e.is_system_critical ? (
                          <svg className="mx-auto h-4 w-4 text-ui-text3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                          </svg>
                        ) : (
                          <span className="text-xs text-ui-text3">—</span>
                        )}
                      </td>
                      <td className="td-cell">
                        <Badge variant={e.is_active ? 'success' : 'neutral'}>
                          {e.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="td-cell">
                        {e.is_system_critical ? (
                          <span className="text-xs text-ui-text3">Locked</span>
                        ) : (
                          <Button variant="link" size="xs" className="text-accent-text" onClick={() => openEdit(e)}>Edit</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-drawer flex justify-end">
          <div className="modal-scrim absolute inset-0" onClick={closeEdit} />
          <div className="relative flex w-full max-w-lg flex-col overflow-y-auto bg-ui-card shadow-pop">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-ui-border px-5 py-4">
              <div>
                <p className="font-mono text-xs text-ui-text2">{editing.notification_code}</p>
                <p className="text-sm font-semibold text-ui-text">Edit Directory Entry</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="text-warning-text"
                  onClick={() => setShowGuide(!showGuide)}
                >
                  ? How this works
                </Button>
                <IconButton label="Close" size="sm" onClick={closeEdit}>
                  {CLOSE_ICON}
                </IconButton>
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-5 px-5 py-4">
              {/* Guide panel */}
              {showGuide && (
                <Alert tone="warning" role="note" className="space-y-2 px-4 py-3">
                  <p className="font-semibold">How notification dispatch works</p>
                  <ol className="list-inside list-decimal space-y-1 leading-relaxed">
                    <li><span className="font-medium">Classification</span> — each directory entry has a default classification (Critical / Important / Info) that determines urgency.</li>
                    <li><span className="font-medium">Channel matrix</span> — the system maps each classification to a set of channels (e.g. Critical fires email + SMS + push). This matrix lives in the <span className="rounded-chip bg-warning/15 px-1 font-mono">notification</span> configuration catalogue.</li>
                    <li><span className="font-medium">Tenant override</span> — when enabled, a tenant can replace the system matrix with their own. When disabled, the system matrix is enforced and mandatory channels always fire regardless of tenant or user preferences.</li>
                    <li><span className="font-medium">Mandatory channels</span> — only relevant when tenant override is OFF. These channels fire unconditionally, even during quiet hours.</li>
                  </ol>
                  <p className="leading-relaxed">At send time: directory entry classification &rarr; resolve channel matrix (system or tenant) &rarr; intersect with user preferences &rarr; apply quiet hours &rarr; dispatch.</p>
                </Alert>
              )}

              {/* Description */}
              {editing.description && (
                <p className="text-xs italic text-ui-text3">{editing.description}</p>
              )}

              {/* Classification */}
              <div>
                <label htmlFor="dir-classification" className="mb-1 block text-xs font-medium text-ui-text">Classification</label>
                <select
                  id="dir-classification"
                  value={editClassification}
                  onChange={(e) => setEditClassification(e.target.value)}
                  className={fieldClass()}
                >
                  <option value="">None</option>
                  {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Tenant Override toggle */}
              <div className="space-y-2 rounded-control border border-ui-border bg-ui-subtle px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium text-ui-text">Tenant Override</p>
                    <p className="mt-0.5 text-xs text-ui-text3">
                      {editTenantOverride
                        ? 'Tenants can configure their own channel matrix in Settings. System defaults are advisory.'
                        : 'System matrix is enforced. Tenant settings are ignored; mandatory channels always fire.'}
                    </p>
                  </div>
                  <Switch checked={editTenantOverride} onCheckedChange={setEditTenantOverride} aria-label="Tenant override" />
                </div>
              </div>

              {/* Channel matrix info box */}
              <div className="space-y-2 rounded-control border border-ui-border bg-ui-subtle px-4 py-3">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0 text-ui-text3" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.75 10.5h-1.5v-5h1.5v5zm0-6.5h-1.5V3.5h1.5V5z"/>
                  </svg>
                  <div>
                    <p className="text-xs font-medium text-ui-text">Channel Matrix</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-ui-text3">
                      The active channel matrix (which channels fire per classification) is stored in the{' '}
                      <span className="font-medium text-ui-text2">notification</span> configuration catalogue — not per directory entry. The system matrix is the platform default; tenants override it in Settings &rarr; Notifications.
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      size="xs"
                      className="mt-2 text-accent-text"
                      onClick={() => navigate('/platform/configurations?prefix=notification')}
                    >
                      Open in Configurations &rarr;
                    </Button>
                  </div>
                </div>
              </div>

              {/* Mandatory channels — only when tenant override is OFF */}
              {!editTenantOverride && (
                <div>
                  <p className="mb-0.5 text-xs font-medium text-ui-text">Mandatory Channels</p>
                  <p className="mb-2 text-xs text-ui-text3">Always fire — cannot be suppressed by any tenant or user setting.</p>
                  <ChannelCheckboxGroup
                    label=""
                    selected={editMandatory}
                    onChange={setEditMandatory}
                  />
                </div>
              )}

              {/* Active toggle */}
              <div className="rounded-control border border-ui-border bg-ui-subtle px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium text-ui-text">Active</p>
                    <p className="mt-0.5 text-xs text-ui-text3">Inactive entries are ignored by the notification engine.</p>
                  </div>
                  <Switch checked={editIsActive} onCheckedChange={setEditIsActive} aria-label="Active" />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 border-t border-ui-border px-5 py-4">
              <Button
                variant="accent"
                className={cn('flex-1', saveOk && 'bg-success-fill hover:shadow-none')}
                onClick={handleSave}
              >
                {saveOk ? 'Saved!' : 'Save'}
              </Button>
              <Button variant="outline" className="flex-1" onClick={closeEdit}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Preferences Tab ───────────────────────────────────────────────────────────

const CHANNEL_LABELS: Record<string, string> = { in_app: 'In-App', push: 'Push', email: 'Email', sms: 'SMS' };

function PreferencesTab() {
  const { data: cfgData, isLoading } = useGetConfigurationQuery('notification');
  const [updateConfig, { isLoading: isSaving }] = useUpdateConfigurationMutation();

  const cfg = (cfgData?.data?.effective_value ?? {}) as Record<string, unknown>;
  const matrix = (cfg.matrix ?? {}) as Record<string, Record<string, boolean>>;
  const quietHours = (cfg.quiet_hours ?? { enabled: false, from: '22:00', to: '07:00' }) as { enabled: boolean; from: string; to: string };
  const timezone = (cfg.timezone ?? 'UTC') as string;

  const [localMatrix, setLocalMatrix] = useState<Record<string, Record<string, boolean>> | null>(null);
  const [localQH, setLocalQH] = useState<{ enabled: boolean; from: string; to: string } | null>(null);
  const [localTz, setLocalTz] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const activeMatrix = localMatrix ?? matrix;
  const activeQH = localQH ?? quietHours;
  const activeTz = localTz ?? timezone;

  const toggleChannel = (cls: string, ch: string) => {
    const current = activeMatrix[cls] ?? {};
    setLocalMatrix({ ...activeMatrix, [cls]: { ...current, [ch]: !current[ch] } });
    setSaved(false);
  };

  const handleSave = async () => {
    await updateConfig({
      key: 'notification',
      value: { ...cfg, matrix: activeMatrix, quiet_hours: activeQH, timezone: activeTz },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (isLoading) {
    return <LoadingState label="Loading..." />;
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {/* Channel delivery matrix */}
      <div className="table-card">
        <div className="border-b border-ui-border bg-ui-subtle px-4 py-3">
          <h3 className="text-sm font-semibold text-ui-text">Channel Delivery Matrix</h3>
          <p className="mt-0.5 text-xs text-ui-text3">Platform-wide defaults. Tenants can override per their settings.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="th-cell w-32">Classification</th>
                {ALL_CHANNELS.map((ch) => (
                  <th key={ch} className="th-cell text-center">{CHANNEL_LABELS[ch] ?? ch}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CLASSIFICATIONS.map((cls) => {
                const row = activeMatrix[cls.toLowerCase()] ?? {};
                return (
                  <tr key={cls} className="transition-colors hover:bg-ui-hover">
                    <td className="td-cell">
                      <Badge variant={CLASSIFICATION_VARIANT[cls]}>{cls}</Badge>
                    </td>
                    {ALL_CHANNELS.map((ch) => (
                      <td key={ch} className="td-cell text-center">
                        <Switch
                          checked={!!row[ch]}
                          onCheckedChange={() => toggleChannel(cls.toLowerCase(), ch)}
                          aria-label={`${cls} via ${CHANNEL_LABELS[ch] ?? ch}`}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quiet hours */}
      <div className="card-surface overflow-hidden">
        <div className="border-b border-ui-border bg-ui-subtle px-4 py-3">
          <h3 className="text-sm font-semibold text-ui-text">Quiet Hours Default</h3>
          <p className="mt-0.5 text-xs text-ui-text3">Platform default. Tenants and users can override. During quiet hours, only in-app notifications are delivered.</p>
        </div>
        <div className="flex flex-wrap items-center gap-6 px-4 py-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ui-text">
            <Switch
              checked={activeQH.enabled}
              onCheckedChange={() => { setLocalQH({ ...activeQH, enabled: !activeQH.enabled }); setSaved(false); }}
            />
            Enabled
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2">
            From
            <input
              type="time"
              value={activeQH.from}
              onChange={(e) => { setLocalQH({ ...activeQH, from: e.target.value }); setSaved(false); }}
              className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2">
            To
            <input
              type="time"
              value={activeQH.to}
              onChange={(e) => { setLocalQH({ ...activeQH, to: e.target.value }); setSaved(false); }}
              className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
            />
          </label>
        </div>
      </div>

      {/* Timezone */}
      <div className="card-surface overflow-hidden">
        <div className="border-b border-ui-border bg-ui-subtle px-4 py-3">
          <h3 className="text-sm font-semibold text-ui-text">Default Timezone</h3>
          <p className="mt-0.5 text-xs text-ui-text3">Platform default for quiet-hours evaluation. Tenants and users can override to their own timezone.</p>
        </div>
        <div className="px-4 py-4">
          <input
            type="text"
            value={activeTz}
            onChange={(e) => { setLocalTz(e.target.value); setSaved(false); }}
            placeholder="e.g. America/New_York"
            aria-label="Default timezone"
            className={cn(fieldClass({ size: 'sm' }), 'w-64')}
          />
          <p className="mt-1.5 text-xs text-ui-text3">IANA timezone string (e.g. UTC, America/Chicago, Europe/London)</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save Changes'}
        </Button>
        {saved && <span className="text-sm text-success-text">Saved</span>}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'requests', label: 'Requests' },
  { key: 'directory', label: 'Directory' },
  { key: 'templates', label: 'Templates' },
  { key: 'preferences', label: 'Preferences' },
] as const;

type TabKey = typeof TABS[number]['key'];

export default function NotificationsAdminPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('requests');

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <PageHeader
          title="Notification Management"
          subtitle="Monitor requests, manage templates, configure directory entries, and inspect queue health."
          toolbar={
            <Tabs
              label="Notification management sections"
              tabs={TABS.map(({ key, label }) => ({ id: key, label }))}
              value={activeTab}
              onChange={(id) => setActiveTab(id as TabKey)}
            />
          }
        />
      }
    >
      {activeTab === 'requests' && <RequestsTab />}
      {activeTab === 'directory' && <DirectoryTab />}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'preferences' && <PreferencesTab />}
    </PageShell>
  );
}
